import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PercentageSlider } from "@/components/form/fields/PercentageSlider";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ContactListItem } from "@/components/form/ContactListItem";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { Plus, Trash2, AlertCircle, ChevronsUpDown, User } from "lucide-react";
import type { PayerRequest } from "@/types/billing";
// Deep-imported (not via the `@/api` barrel) so a `vi.mock('@/api')` in a test that
// renders this editor cannot leave the enum object `undefined` at module load.
import { billing_PayerRequest } from "@/api/models/billing_PayerRequest";
import type { Contact } from "@/types/contact";

interface PayerSplitEditorProps {
  payers: PayerRequest[];
  onPayersChange: (payers: PayerRequest[]) => void;
  contacts?: Contact[];
}

export function PayerSplitEditor({
  payers,
  onPayersChange,
  contacts = [],
}: PayerSplitEditorProps) {
  const { i18n } = useLingui();

  // Log payers state for debugging

  // State for managing popover open states for each payer
  const [openPopovers, setOpenPopovers] = useState<Record<number, boolean>>({});
  const [searchQueries, setSearchQueries] = useState<Record<number, string>>(
    {}
  );

  const totalPercentage = payers.reduce(
    (sum, p) => sum + (p.percentage_share || 0),
    0
  );
  const isValid = totalPercentage === 100;

  // Removed primary payer normalization - no longer needed

  // Removed primary payer effect - no longer needed

  const setPopoverOpen = (index: number, open: boolean) => {
    setOpenPopovers((prev) => ({ ...prev, [index]: open }));
  };

  const setSearchQuery = (index: number, query: string) => {
    setSearchQueries((prev) => ({ ...prev, [index]: query }));
  };

  // Filter contacts based on search query and exclude already selected contacts
  const getFilteredContacts = (index: number) => {
    const query = searchQueries[index]?.toLowerCase() || "";

    // Get IDs of all already selected contacts (excluding the current payer being edited)
    const selectedContactIds = new Set(
      payers
        .filter((_, payerIndex) => payerIndex !== index)
        .map((payer) => payer.contact_id)
        .filter((id) => id) // Remove null/undefined values
    );

    // Filter out already selected contacts
    let availableContacts = contacts.filter(
      (contact) => !selectedContactIds.has(contact.id)
    );

    // Apply search query filter if present
    if (query) {
      availableContacts = availableContacts.filter(
        (contact) =>
          contact.firstname?.toLowerCase().includes(query) ||
          contact.lastname?.toLowerCase().includes(query) ||
          contact.emailMain?.toLowerCase().includes(query)
      );
    }

    return availableContacts;
  };

  const addPayer = () => {
    // Don't add a payer if no contacts are available
    if (contacts.length === 0) {
      return;
    }

    const remainingPercentage = Math.max(0, 100 - totalPercentage);
    const newPayer: PayerRequest = {
      contact_id: "",
      percentage_share: remainingPercentage,
      // Required by `billing_PayerRequest`. This editor only ever adds a
      // contact-backed payer, so CONTACT is the only correct value here.
      payer_type: billing_PayerRequest.payer_type.CONTACT,
    };

    onPayersChange([...payers, newPayer]);
  };

  const removePayer = (index: number) => {
    const updated = payers.filter((_, i) => i !== index);
    onPayersChange(updated);
  };

  const updatePayer = (index: number, updates: Partial<PayerRequest>) => {
    const updated = [...payers];
    updated[index] = { ...updated[index], ...updates };
    // Default to CONTACT if not specified
    if (!updated[index].payer_type) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updated[index].payer_type = "CONTACT";
    }
    onPayersChange(updated);
  };

  const autoDistribute = () => {
    if (payers.length === 0) return;
    const equalShare = Math.floor(100 / payers.length);
    const remainder = 100 - equalShare * payers.length;

    const updated = payers.map((p, i) => ({
      ...p,
      percentage_share: equalShare + (i === 0 ? remainder : 0),
    }));

    onPayersChange(updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">
          {i18n._(msg`Payer Configuration`)}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {i18n._(msg`Configure who pays for this contract and their split`)}
        </p>
      </div>

      {/* Payer Cards */}
      <div className="space-y-3">
        {payers.map((payer, index) => (
          <Card key={index}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  {i18n._(msg`Payer ${index + 1}`)}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePayer(index)}
                    aria-label={i18n._({ id: "contracts.payerSplit.remove", message: "Remove payer" })}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact and Payment Split on same row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Contact Selection */}
                <div>
                  <Label>{i18n._(msg`Contact`)}</Label>
                  <Popover
                    open={openPopovers[index] || false}
                    onOpenChange={(open) => setPopoverOpen(index, open)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={openPopovers[index] || false}
                        className="w-full justify-between mt-1"
                      >
                        {(() => {
                          const selectedContact = payer.contact_id
                            ? contacts.find((c) => c.id === payer.contact_id)
                            : undefined;
                          return selectedContact ? (
                            <span className="flex items-center gap-2 truncate">
                              <User className="h-4 w-4 shrink-0" />
                              <span className="truncate">
                                {selectedContact.firstname}{" "}
                                {selectedContact.lastname}
                                {selectedContact.emailMain && (
                                  <span className="text-muted-foreground ml-2">
                                    ({selectedContact.emailMain})
                                  </span>
                                )}
                              </span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">
                              {i18n._(msg`Choose a contact...`)}
                            </span>
                          );
                        })()}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-full p-0"
                      align="start"
                      style={{ width: "var(--radix-popover-trigger-width)" }}
                    >
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder={i18n._(msg`Search by name or email...`)}
                          value={searchQueries[index] || ""}
                          onValueChange={(query) =>
                            setSearchQuery(index, query)
                          }
                        />
                        <CommandList className="max-h-[300px] overflow-y-auto">
                          {(() => {
                            const filteredContacts = getFilteredContacts(index);

                            if (filteredContacts.length === 0) {
                              return (
                                <CommandEmpty>
                                  <div className="text-center py-4">
                                    <p className="text-sm text-muted-foreground">
                                      {i18n._(msg`No contacts found`)}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {contacts.length === 0
                                        ? i18n._(
                                            msg`No contacts available in the system`
                                          )
                                        : i18n._(
                                            msg`Try adjusting your search`
                                          )}
                                    </p>
                                  </div>
                                </CommandEmpty>
                              );
                            }

                            return (
                              <CommandGroup>
                                {filteredContacts.map((contact) => (
                                  <CommandItem
                                    key={contact.id}
                                    value={contact.id}
                                    onSelect={(currentValue) => {
                                      updatePayer(index, {
                                        contact_id: currentValue,
                                      });
                                      setPopoverOpen(index, false);
                                    }}
                                  >
                                    <ContactListItem
                                      contact={contact}
                                      selected={payer.contact_id === contact.id}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            );
                          })()}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Payment Split */}
                <div>
                  <Label>{i18n._(msg`Payment Split`)}</Label>
                  <div className="mt-1">
                    <PercentageSlider
                      field={{
                        name: "percentage_share",
                        // `FieldConfig` requires these; `PercentageSlider` renders its own
                        // label from the `<Label>` above and ignores both.
                        label: "",
                        type: "percentage",
                      }}
                      value={payer.percentage_share || 0}
                      onChange={(value) =>
                        updatePayer(index, { percentage_share: value })
                      }
                      id={`payer-percentage-${index}`}
                      min={0}
                      max={100}
                      step={1}
                      showDecimals={false}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-2">
        {/* Show message when no contacts are available */}
        {contacts.length === 0 && (
          <div className="flex items-center gap-2 p-3 bg-warning-muted border border-warning-on-surface/30 rounded-md">
            <AlertCircle className="h-4 w-4 text-capacity-warning" />
            <div className="text-sm text-warning-on-surface">
              <p className="font-medium">
                {i18n._(msg`No contacts available`)}
              </p>
              <p className="text-xs mt-1">
                {i18n._(
                  msg`Please ensure the selected child has associated contacts before adding payers.`
                )}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={addPayer}
            disabled={contacts.length === 0}
            title={
              contacts.length === 0
                ? i18n._(msg`No contacts available for the selected child`)
                : undefined
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            {i18n._(msg`Add Payer`)}
          </Button>
          {payers.length > 1 && (
            <Button type="button" variant="outline" onClick={autoDistribute}>
              {i18n._(msg`Distribute Equally`)}
            </Button>
          )}
        </div>
      </div>

      {/* Visual Split Display */}
      {payers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {i18n._(msg`Payment Split`)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 h-8 rounded-md overflow-hidden">
              {payers.map((payer, index) => {
                const contact = contacts.find((c) => c.id === payer.contact_id);
                // Payers are series, not states: the chart palette follows the theme.
                const colors = [
                  "bg-[hsl(var(--chart-1))]",
                  "bg-[hsl(var(--chart-2))]",
                  "bg-[hsl(var(--chart-3))]",
                  "bg-[hsl(var(--chart-4))]",
                ];
                return (
                  <div
                    key={index}
                    className={`${
                      colors[index % colors.length]
                    } flex items-center justify-center text-white text-xs font-medium`}
                    style={{ width: `${payer.percentage_share}%` }}
                  >
                    {payer.percentage_share > 10 && (
                      <span>
                        {contact?.firstname?.charAt(0) ?? "P"}
                        {index + 1}: {payer.percentage_share}%
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Message - Always at the bottom */}
      {payers.length > 0 && !isValid && (
        <div className="flex items-center gap-2 p-3 bg-warning-muted border border-warning-on-surface/30 rounded-md">
          <AlertCircle className="h-4 w-4 text-capacity-warning" />
          <span className="text-sm text-warning-on-surface">
            {totalPercentage < 100
              ? i18n._(
                  msg`Total is ${totalPercentage}%. Add ${
                    100 - totalPercentage
                  }% more.`
                )
              : i18n._(
                  msg`Total exceeds 100% (${totalPercentage}%). Reduce by ${
                    totalPercentage - 100
                  }%.`
                )}
          </span>
        </div>
      )}
    </div>
  );
}
